import * as Calendar from 'expo-calendar'

/** What happened, so the screen can say the right thing without knowing about calendars. */
export type CalendarResult = 'added' | 'denied' | 'failed'

/**
 * The fields an entry is written from, rather than a whole payload.
 *
 * It took a `BookedVisit` while the booked screen was the only caller. The visit page offers the
 * same thing from an `UpcomingVisit`, which names its type differently and carries no timezone,
 * and the shorter way to share this was to ask for the five facts a calendar entry is made of.
 */
export type CalendarVisit = {
	/** What the entry is called, after the brand: "Perio Evaluation". */
	name: string
	startTimeUtc: string
	/**
	 * When it ends, by whichever of the two the payload carries.
	 *
	 * The booked visit sends a length and the upcoming one sends an end, and both are the same
	 * fact. Where a payload carries neither the entry is written as half an hour, which is the
	 * shortest visit the studio books — a calendar entry of no length is worse than one that is
	 * roughly right.
	 */
	durationInMinutes?: number
	endTimeUtc?: string
	address: string
	/** The studio, as the entry's note. Absent on an older upcoming-visit payload. */
	locationName?: string
	/**
	 * The studio's zone, where the payload carries one.
	 *
	 * Without it the entry is still at the right instant — it is written from `startTimeUtc` —
	 * and the phone labels it in its own zone, which is what a member travelling would want
	 * anyway.
	 */
	timezone?: string
}

const MINUTE = 60 * 1000

/**
 * Put a booked visit in the phone's own calendar.
 *
 * Written off `startTimeUtc` rather than the local string the screen prints. The studio's wall
 * clock is what a member reads, but a calendar entry has to survive them travelling — an instant
 * plus the studio's zone stays correct in a way "12:30" does not.
 *
 * The permission is asked for here, on the tap, rather than at launch: a member who never books
 * is never asked, and one who does is asked while the reason is on the screen in front of them.
 */
export const addVisitToCalendar = async (
	visit: CalendarVisit
): Promise<CalendarResult> => {
	try {
		const { status } = await Calendar.requestCalendarPermissionsAsync()
		if (status !== Calendar.PermissionStatus.GRANTED) {
			return 'denied'
		}

		const calendar = await Calendar.getDefaultCalendarAsync()
		if (!calendar?.id) {
			return 'failed'
		}

		const start = new Date(visit.startTimeUtc)
		const end = visit.endTimeUtc
			? new Date(visit.endTimeUtc)
			: new Date(start.getTime() + (visit.durationInMinutes ?? 30) * MINUTE)
		await Calendar.createEventAsync(calendar.id, {
			title: `Wally — ${visit.name}`,
			startDate: start,
			endDate: end,
			location: visit.address,
			timeZone: visit.timezone,
			notes: visit.locationName ?? 'Wally',
		})
		return 'added'
	} catch {
		return 'failed'
	}
}
